import { ref } from 'vue'
import { defineStore } from 'pinia'
import { TRANSLATIONS_API_URL, TRANSLATIONS_HASH_URL } from '@/vue/helpers/apiURLS'
import getApiData from '@/vue/helpers/getApiData'
import { useToast } from '../components/design-system/toaster'
import { useToastStore } from './toastStore'

interface ChartExplanation {
    key: string
    published: boolean
    description: Record<string, { section: string }[] | { section: string }>
    position: string
}

type SupportedLcales = 'zh-CN' | 'de-DE' | 'en-US' | 'es-ES' | 'fr-FR' | 'ja-JP' | 'pt-PT'

interface TranslationData {
    locale: SupportedLcales
    translations: Record<string, string>
    chart_explanations: Record<string, ChartExplanation>
}

export const useTranslationsStore = defineStore('translationsStore', () => {
    const translationData = ref<TranslationData>({
        locale: 'en-US',
        translations: {},
        chart_explanations: {},
    })
    const storeInitialized = ref<boolean>(false)
    const { toast } = useToast()

    async function initializeStore(): Promise<void> {
        const translationsPromise = getTranslations(null, true)
        const storedTranslationData = localStorage.getItem('translationData')

        if (storedTranslationData !== null) {
            /*
             * If we already have cached translation data, set it to state and return early. translationsPromise will
             * eventually resolve and update translationData if needed.
             */
            translationData.value = JSON.parse(storedTranslationData)
            storeInitialized.value = true
            return
        }

        // We don't have local translation data, wait until we do to set initialized state
        await translationsPromise
    }

    const storedData = localStorage.getItem('translationData')

    if (storedData !== null) {
        translationData.value = JSON.parse(storedData)
    }

    function getDateTimeFormat(options: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat {
        return new Intl.DateTimeFormat(translationData.value.locale, options)
    }

    async function getTranslations(hashFromApi: string | null = null, skipInitialization: boolean = false): Promise<void> {
        if (!hashFromApi) {
            const responseHash = await getApiData(TRANSLATIONS_HASH_URL)
            hashFromApi = responseHash.data?.data?.hash ?? null
        }
        const translationDataHash = localStorage.getItem('translationDataHash')
        const storedTranslationData = localStorage.getItem('translationData')
        if (storedTranslationData && translationDataHash === hashFromApi) {
            // Avoid parsing storeTranslationData as JSON.parse blocks the main thread
            if (!skipInitialization) {
                translationData.value = JSON.parse(storedTranslationData)
                storeInitialized.value = true
            }
            return
        }
        try {
            const translationDataResponse = await getApiData(TRANSLATIONS_API_URL)
            if (!translationDataResponse.data.success) {
                toast.error({
                    description: translationDataResponse.data.data.message,
                })
                return
            }
            localStorage.setItem('translationData', JSON.stringify(translationDataResponse.data.data))
            localStorage.setItem('translationDataHash', hashFromApi as string)
            translationData.value = translationDataResponse.data.data
            storeInitialized.value = true
        } catch {
            // TODO handle error correctly toast or something
        }
    }

    function translate(key: string, params: Record<string, string | number> | null = null): string {
        let response = translationData.value['translations']?.[key] || ''
        if (params) {
            for (const [k, v] of Object.entries(params)) {
                if (typeof v === 'string') {
                    response = response.replace(`:${k}`, v)
                } else {
                    // v should, of course, not be null or undefined here, but if often is.
                    response = response.replace(`:${k}`, v?.toString() ?? '')
                }
            }
        }
        return response
    }

    function getChartExplanation(key: string): ChartExplanation | null {
        if (translationData?.value?.chart_explanations?.[key]) {
            return translationData.value.chart_explanations[key]
        }
        return null
    }

    function capitalize(string: string): string {
        const words = string.split(' ')
        for (let i = 0; i < words.length; i++) {
            if (words[i].length) {
                words[i] = words[i][0]?.toUpperCase() + words[i].substr(1)
            }
        }

        return words.join(' ')
    }

    return {
        // state
        storeInitialized,
        translationData,
        // actions
        initializeStore,
        getDateTimeFormat,
        getTranslations,
        translate,
        getChartExplanation,
        capitalize,
    }
})
